import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { submitCollegeSession, isValidIndianMobile, isValidEmail } from "@/lib/api";
import { EXPERT_TOPICS } from "@/lib/site";

const initial = {
  collegeName: "", contactPerson: "", designation: "", mobile: "", email: "",
  city: "", numberOfStudents: "", preferredTopic: "", preferredDate: "", message: "",
};

export const CollegeSessionForm = ({ onDark = true }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.collegeName.trim()) e.collegeName = "College / University name is required.";
    if (!form.contactPerson.trim()) e.contactPerson = "Contact person is required.";
    if (!form.designation.trim()) e.designation = "Designation is required.";
    if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.city.trim()) e.city = "City is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitCollegeSession(form);
      toast.success(res.message || "Request submitted!");
      setDone(true);
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const wrap = onDark ? "border-white/10 bg-white/[0.03]" : "border-navy/10 bg-white";

  if (done) {
    return (
      <div data-testid="college-form-success" className={`flex flex-col items-center gap-4 rounded-2xl border p-10 text-center ${wrap}`}>
        <CheckCircle2 className="h-12 w-12 text-orange" />
        <h3 className={`font-heading text-2xl ${onDark ? "text-white" : "text-navy"}`}>Request received!</h3>
        <p className={`text-sm ${onDark ? "text-white/70" : "text-navy/70"}`}>Our team will reach out to coordinate the expert session.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="college-session-form" className={`flex flex-col gap-4 rounded-2xl border p-6 sm:p-8 ${wrap}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput label="College / University Name" id="cs-college" value={form.collegeName} onChange={set("collegeName")} error={errors.collegeName} onDark={onDark} placeholder="Institution name" testId="cs-collegeName" />
        <FormInput label="Contact Person" id="cs-person" value={form.contactPerson} onChange={set("contactPerson")} error={errors.contactPerson} onDark={onDark} placeholder="Full name" testId="cs-contactPerson" />
        <FormInput label="Designation" id="cs-desig" value={form.designation} onChange={set("designation")} error={errors.designation} onDark={onDark} placeholder="e.g. HOD, TPO" testId="cs-designation" />
        <FormInput label="Mobile Number" id="cs-mobile" value={form.mobile} onChange={set("mobile")} error={errors.mobile} onDark={onDark} placeholder="10-digit mobile" testId="cs-mobile" />
        <FormInput label="Email" id="cs-email" type="email" value={form.email} onChange={set("email")} error={errors.email} onDark={onDark} placeholder="you@college.edu" testId="cs-email" />
        <FormInput label="City" id="cs-city" value={form.city} onChange={set("city")} error={errors.city} onDark={onDark} placeholder="City" testId="cs-city" />
        <FormInput label="Number of Students" id="cs-num" value={form.numberOfStudents} onChange={set("numberOfStudents")} onDark={onDark} placeholder="e.g. 120" testId="cs-numberOfStudents" />
        <div className="flex flex-col gap-2">
          <label htmlFor="cs-topic" className={`text-xs font-semibold uppercase tracking-wider ${onDark ? "text-white/70" : "text-navy/70"}`}>Preferred Topic</label>
          <select id="cs-topic" value={form.preferredTopic} onChange={set("preferredTopic")} data-testid="cs-preferredTopic" className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition-colors ${onDark ? "border-white/15 bg-white/5 text-white focus:border-orange" : "border-navy/15 bg-white text-navy focus:border-royal"}`}>
            <option value="">Select a topic</option>
            {EXPERT_TOPICS.map((t) => (<option key={t} value={t} className="text-navy">{t}</option>))}
          </select>
        </div>
        <FormInput label="Preferred Date" id="cs-date" type="date" value={form.preferredDate} onChange={set("preferredDate")} onDark={onDark} testId="cs-preferredDate" />
      </div>
      <FormInput label="Message (optional)" id="cs-msg" as="textarea" value={form.message} onChange={set("message")} onDark={onDark} placeholder="Any specific requirements?" testId="cs-message" />
      <PrimaryButton type="submit" size="lg" disabled={submitting} testId="cs-submit">
        {submitting ? "Sending…" : "Request Expert Session"}
      </PrimaryButton>
    </form>
  );
};
